

import { useState, useEffect, useMemo } from "react";
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Tags
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";

// Types
interface ExpenseCategory {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  is_system?: boolean;
  expenses_count?: number;
  created_at?: string;
}

// Reusable Action Button Component
function ActionButton({ 
  onClick, 
  icon: Icon, 
  label, 
  variant = 'default' 
}: { 
  onClick: () => void; 
  icon: any; 
  label: string;
  variant?: 'default' | 'primary' | 'danger' | 'success';
}) {
  const colors = {
    default: 'text-stone-600 hover:bg-stone-100',
    primary: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-emerald-600 hover:bg-emerald-50',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Expense Category Form Component
function ExpenseCategoryForm({ 
  initialData, 
  onSubmit, 
  onCancel,
}: { 
  initialData?: Partial<ExpenseCategory>; 
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    icon: initialData?.icon || "",
  });

  // Reset form when initialData changes (for new entries)
  useEffect(() => {
    if (!initialData?.id) {
      setFormData({
        name: "",
        description: "",
        icon: "",
      });
    } else {
      setFormData({
        name: initialData.name || "",
        description: initialData.description || "",
        icon: initialData.icon || "",
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      description: formData.description,
      icon: formData.icon,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Category Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter category name"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Category description"
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update" : "Add"} Category
        </button>
      </div>
    </form>
  );
}

import { withAuth } from "@/app/components/withAuth";

function ExpenseCategoriesPage() {
  const { showToast } = useToast();
  
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null);
  
  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<ExpenseCategory | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.expenseCategories.list();
      setCategories(response.data?.data || response.data || []);
    } catch (error) {
      showToast("Failed to load categories", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categories;
    const q = searchQuery.toLowerCase();
    return categories.filter(c => 
      c.name.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q)
    );
  }, [categories, searchQuery]);

  const openDeleteModal = (category: ExpenseCategory) => {
    if (category.is_system) {
      showToast("System categories cannot be deleted", "error");
      return;
    }
    setDeletingCategory(category);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingCategory) return;
    
    setDeleteLoading(true);
    try {
      await mastersApi.expenseCategories.delete(deletingCategory.id);
      showToast("Category deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingCategory(null);
      loadData(); // Reload data to ensure consistency
    } catch (error: any) {
      const errorMessage = error.message || "Failed to delete category";
      showToast(errorMessage, "error");
      setDeleteModalOpen(false);
      setDeletingCategory(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingCategory) {
        // Update existing
        await mastersApi.expenseCategories.update(editingCategory.id, data);
        showToast("Category updated successfully", "success");
      } else {
        // Create new
        await mastersApi.expenseCategories.create(data);
        showToast("Category created successfully", "success");
      }
      
      // Close modal and reset form
      setIsModalOpen(false);
      setEditingCategory(null);
      loadData(); // Reload to get fresh data
    } catch (error) {
      showToast("Failed to save category", "error");
      loadData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Expense Categories</h1>
        <p className="text-sm text-stone-500">Manage expense categories</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search categories..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => { setEditingCategory(null); setIsModalOpen(true); }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {/* Content */}
      {filteredCategories.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Tags className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No categories found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Description</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Expenses</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredCategories.map((category) => (
                  <tr key={category.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-stone-800">{category.name}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">{category.description || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      {category.is_system ? (
                        <span className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-lg">System</span>
                      ) : (
                        <span className="text-xs bg-amber-50 text-amber-600 px-2 py-1 rounded-lg">Custom</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">{category.expenses_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <ActionButton 
                          onClick={() => { setEditingCategory(category); setIsModalOpen(true); }} 
                          icon={Pencil}
                          label="Edit"
                          variant="default"
                        />
                        {!category.is_system && (
                          <ActionButton 
                            onClick={() => openDeleteModal(category)} 
                            icon={Trash2}
                            label="Delete" 
                            variant="danger"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingCategory(null); }}
        title={editingCategory ? "Edit Category" : "Add New Category"}
        size="md"
      >
        <ExpenseCategoryForm
          initialData={editingCategory || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingCategory(null); }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingCategory(null); }}
        onConfirm={handleDelete}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone."
        itemName={deletingCategory?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}

export default withAuth(ExpenseCategoriesPage, { allowedRoles: ['admin', 'super_admin', 'manager'] });
